<template>
    <div
        class="flex h-auto w-full flex-col items-start justify-start space-y-4 p-4"
    >
        <!-- Status -->
        <SelectField
            v-model="form.status"
            name="status"
            label="Status"
            :required="true"
            :options="[
                { label: 'Draft', value: 'draft' },
                { label: 'Published', value: 'published' },
            ]"
            class="w-full"
        />

        <!-- Basic SEO -->
        <TextField
            v-model="form.title[activeLocale]"
            :name="`title-${activeLocale}`"
            label="Title"
            :required="true"
            class="w-full"
        />
        <div class="w-full">
            <div class="mb-1 flex items-center justify-between">
                <label
                    class="block text-sm font-medium"
                    for="description"
                    >Description</label
                >
                <span
                    class="text-xs"
                    :class="
                        form.description[activeLocale].length > 160
                            ? 'text-red-500'
                            : 'text-slate-400'
                    "
                >
                    {{ form.description[activeLocale].length }} / 160
                </span>
            </div>
            <textarea
                id="description"
                v-model="form.description[activeLocale]"
                :name="`description-${activeLocale}`"
                class="form-input w-full"
                rows="3"
            />
        </div>
        <div class="w-full">
            <div class="mb-1 flex items-center justify-between">
                <label
                    class="block text-sm font-medium"
                    for="slug"
                    >Slug</label
                >
                <span
                    v-if="!slugManuallyEdited"
                    class="text-xs text-slate-400"
                    >Auto</span
                >
            </div>
            <input
                id="slug"
                v-model="form.slug[activeLocale]"
                :name="`slug-${activeLocale}`"
                type="text"
                class="form-input w-full"
                placeholder="e.g. about-us"
            />
        </div>
        <div class="w-full">
            <label
                class="mb-1 block text-sm font-medium"
                for="focus_keyword"
                >Focus Keyword</label
            >
            <input
                id="focus_keyword"
                v-model="form.focus_keyword[activeLocale]"
                :name="`focus_keyword-${activeLocale}`"
                type="text"
                class="form-input w-full"
                placeholder="e.g. sustainable investing"
            />
        </div>

        <!-- Open Graph -->
        <div class="w-full border-t border-slate-200 pt-4">
            <p
                class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400"
            >
                Open Graph
            </p>

            <div class="space-y-4">
                <div class="w-full">
                    <div class="mb-1 flex items-center justify-between">
                        <label
                            class="block text-sm font-medium"
                            for="og_title"
                            >OG Title</label
                        >
                        <span
                            class="text-xs"
                            :class="
                                form.og_title[activeLocale].length > 60
                                    ? 'text-red-500'
                                    : 'text-slate-400'
                            "
                        >
                            {{ form.og_title[activeLocale].length }} / 60
                        </span>
                    </div>
                    <input
                        id="og_title"
                        v-model="form.og_title[activeLocale]"
                        :name="`og_title-${activeLocale}`"
                        type="text"
                        class="form-input w-full"
                        placeholder="Defaults to title if empty"
                    />
                </div>

                <div class="w-full">
                    <div class="mb-1 flex items-center justify-between">
                        <label
                            class="block text-sm font-medium"
                            for="og_description"
                            >OG Description</label
                        >
                        <span
                            class="text-xs"
                            :class="
                                form.og_description[activeLocale].length > 160
                                    ? 'text-red-500'
                                    : 'text-slate-400'
                            "
                        >
                            {{ form.og_description[activeLocale].length }} / 160
                        </span>
                    </div>
                    <textarea
                        id="og_description"
                        v-model="form.og_description[activeLocale]"
                        :name="`og_description-${activeLocale}`"
                        class="form-input w-full"
                        rows="3"
                        placeholder="Defaults to description if empty"
                    />
                </div>

                <div class="w-full">
                    <label class="mb-1 block text-sm font-medium"
                        >OG Image</label
                    >
                    <div
                        v-if="form.og_image"
                        class="mb-2 space-y-2"
                    >
                        <div
                            class="relative w-full overflow-hidden rounded-lg bg-slate-100"
                            style="aspect-ratio: 16/9"
                        >
                            <img
                                :src="form.og_image.asset.url"
                                :alt="form.og_image.alt"
                                class="h-full w-full object-contain"
                            />
                            <div class="absolute right-2 top-2 flex gap-1">
                                <button
                                    type="button"
                                    class="rounded bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600"
                                    @click="fileUploadRef?.openModal()"
                                >
                                    Update
                                </button>
                                <button
                                    type="button"
                                    class="rounded bg-red-500 px-2 py-1 text-xs font-medium text-white hover:bg-red-600"
                                    @click="
                                        ;(form.og_image = null) && ogImageKey++
                                    "
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                        <input
                            v-model="form.og_image.alt"
                            type="text"
                            class="form-input w-full"
                            placeholder="Alt text"
                        />
                    </div>
                    <div v-show="!form.og_image">
                        <FileUploadForm
                            :key="ogImageKey"
                            ref="fileUploadRef"
                            @select="onImageSelect"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import TextField from '@src/components/TextField.vue'
import SelectField from '@src/components/SelectField.vue'
import FileUploadForm from './FileUploadForm.vue'
import type { ImageField, LocaleMap, MediaAsset } from '@builder/types'
import { useEventBus } from '@vueuse/core'
import { emptyLocaleMap, SUPPORTED_LOCALES } from '@builder/defaults'

const props = defineProps<{
    activeLocale: (typeof SUPPORTED_LOCALES)[number]
}>()

const fileUploadRef = ref<InstanceType<typeof FileUploadForm> | null>(null)
const ogImageKey = ref(0)

const seoBus = useEventBus('seoActions')

const form = reactive({
    status: 'draft' as 'draft' | 'published',
    title: emptyLocaleMap() as LocaleMap,
    description: emptyLocaleMap() as LocaleMap,
    slug: emptyLocaleMap() as LocaleMap,
    og_title: emptyLocaleMap() as LocaleMap,
    og_description: emptyLocaleMap() as LocaleMap,
    og_image: null as ImageField | null,
    focus_keyword: emptyLocaleMap() as LocaleMap,
})

const slugManuallyEdited = ref(false)
const isAutoFillingSlug = ref(false)

const toSlug = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/^-+|-+$/g, '')

seoBus.on((event) => {
    const e = event as { type?: string; data?: typeof form }
    if (e.type === 'init' && e.data) {
        console.log(e.data)
        Object.assign(form, e.data)
        if (e.data.slug) slugManuallyEdited.value = true
    }
})

// watch(
//     () => form.title,
//     (title) => {
//         if (!slugManuallyEdited.value) {
//             isAutoFillingSlug.value = true
//             form.slug = toSlug(title)
//             isAutoFillingSlug.value = false
//         }
//     }
// )

/// Emit form data to seoBus needs to be debounced
watch(form, (value) => seoBus.emit({ ...value }), { deep: true })

const onImageSelect = (asset: MediaAsset) => {
    form.og_image = { asset, alt: '' }
}
</script>
